import React, { Fragment, useCallback, useContext, useMemo, useEffect } from 'react';
import { Button, Spinner, ContentCards, Carousel } from '@hz-design-system/web-ui';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import Cookies from 'js-cookie';

import { TFeedItem } from 'types/TFeeds';
import { TMpCard } from 'types/TMpCard';

import FeedItem from './FeedItem';
import FeedItemListSkeleton from './FeedItemListSkeleton';

import { bannerItemPositionInFeeds, showBannersInFeeds, isBrandTilePosition } from '../Banners/utils/banners';
import BannerContainer from '../Banners/BannerContainer';
import BrandTileBanner from '../Banners/BrandTileBanner';

import { trackGAEvent } from '../../../../utils/gaTracking';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';

import Classes from './FeedItemList.scss';
import MpCard from '../Marketing/MpCard';
import FEED_ITEMS_PER_PAGE from '../../constants/feedItemsPerPage';
import isCarItem from './utils/isCarItem';
import isDesktop from './utils/isDesktop';

/**
 * On average top 2 items are immediately visible on mobile, so lazy loading should be disabled for them.
 */
const FIRST_VISIBLE_ITEMS = 2;
const MIN_CAR_ITEMS_FOR_CAROUSEL = 4;
const MAX_CAR_ITEMS_FOR_CAROUSEL = 10;

const isInFirstVisibleViewport = (itemIndex: number): boolean => itemIndex < FIRST_VISIBLE_ITEMS;

type TFeedsItemsProps = {
  activeFeedIndex: number;
  items: TFeedItem[];
  isLoading: boolean;
  hasNextPage: boolean;
  onLoadMore: (number?) => void;
  feedType: string;
  mpCards?: TMpCard[];
  feedPostCode?: string;
  setShowLoginDialog: Function;
};

/**
 * Banner position cn be calculated only client-side
 *
 * `typeof window !== 'undefined'` check is used because client-side only code is executed outside of `useEffect`
 * `useEffect` is not used here to prevent one extra re-render
 */
const bannerItemPositionInFeedsOnClient = (
  isMpCardSingleTileEnabled: boolean,
  isMpCardDoubleTileEnabled: boolean,
  isBrandTileEnabled: boolean,
) => {
  return typeof window !== 'undefined'
    ? bannerItemPositionInFeeds(isMpCardSingleTileEnabled, isMpCardDoubleTileEnabled, isBrandTileEnabled)
    : undefined;
};

const FeedItemList = ({
  activeFeedIndex,
  items,
  hasNextPage,
  isLoading,
  onLoadMore,
  feedType,
  mpCards,
  feedPostCode,
  setShowLoginDialog,
}: TFeedsItemsProps) => {
  const { t } = useI18nContext();
  const { experiments } = useContext(EnvironmentContext);
  const { isMpCardSingleTileEnabled, isMpCardDoubleTileEnabled, isCarsCarouselEnabled, isBrandTileBannerEnabled } =
    experiments;
  const bannerItemPosition = useMemo(
    () =>
      bannerItemPositionInFeedsOnClient(isMpCardSingleTileEnabled, isMpCardDoubleTileEnabled, isBrandTileBannerEnabled),
    [isMpCardSingleTileEnabled, isMpCardDoubleTileEnabled, isBrandTileBannerEnabled],
  );
  const isMpCardExperimentEnabled = isMpCardSingleTileEnabled || isMpCardDoubleTileEnabled;

  const [sentryRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore,
    rootMargin: '0px 0px 1000px 0px',
    delayInMs: 100,
  });

  const carItems = useMemo(() => {
    const firstBatch = items.slice(0, Math.min(FEED_ITEMS_PER_PAGE, items.length));
    return firstBatch.filter(isCarItem).slice(0, MAX_CAR_ITEMS_FOR_CAROUSEL);
  }, [items]);

  const shouldShowCarousel = carItems.length >= MIN_CAR_ITEMS_FOR_CAROUSEL && isDesktop() && isCarsCarouselEnabled;

  useEffect(() => {
    if (carItems.length > 0 && activeFeedIndex === 0) {
      trackGAEvent('AutoFeedCount', String(carItems.length));
    }
  }, [carItems.length, activeFeedIndex]);

  const handleItemClick = useCallback(
    (correlationId) => {
      Cookies.set('searchRequestId', correlationId);
      trackGAEvent('Clicked', feedType);
    },
    [feedType],
  );

  const handleCarouselItemClick = useCallback((correlationId) => {
    Cookies.set('searchRequestId', correlationId);
    trackGAEvent('CarouselClick', 'Auto');
  }, []);

  const itemsToDisplay = useMemo(() => {
    if (shouldShowCarousel && activeFeedIndex === 0) {
      const carItemIds = new Set(carItems.map((item) => item.itemId));
      return items.filter((item) => !carItemIds.has(item.itemId));
    }
    return items;
  }, [items, carItems, shouldShowCarousel, activeFeedIndex]);

  const getMpCard = (position: number) => {
    if (position !== 0 || activeFeedIndex !== 0 || !isMpCardExperimentEnabled) {
      return null;
    }

    let cardId: string | null = null;
    if (isMpCardSingleTileEnabled) {
      cardId = 'mp-cards-homepage-one-tile';
    } else if (isMpCardDoubleTileEnabled) {
      cardId = 'mp-cards-homepage-two-tiles';
    }

    return mpCards?.find((card) => card.id === cardId);
  };

  if (!items.length && !isLoading) {
    return <Trans className={Classes.message} i18nKey="homepage.feeds.empty" />;
  }

  if (!items.length) {
    return (
      <div className={Classes.container}>
        <FeedItemListSkeleton />
      </div>
    );
  }

  return (
    <div className={Classes.container}>
      {shouldShowCarousel && activeFeedIndex === 0 && (
        <>
          <Carousel title={t('carousel.title')} className={Classes.carousel}>
            {carItems.map((item, i) => (
              <FeedItem
                className={Classes.carouselItem}
                key={`carousel-${item.itemId}-${i}`}
                item={item}
                lazyLoadImage={!isInFirstVisibleViewport(i)}
                onClick={handleCarouselItemClick}
                feedType={feedType}
                feedPostCode={feedPostCode}
                setShowLoginDialog={setShowLoginDialog}
              />
            ))}
          </Carousel>
        </>
      )}
      {Boolean(itemsToDisplay.length) && (
        <ContentCards kind="grid">
          {itemsToDisplay.map((item, i) => {
            const mpCard = getMpCard(i);
            const canRenderFeedBanner = typeof window !== 'undefined' && showBannersInFeeds() && activeFeedIndex <= 1;
            // Render brand tile at all valid positions (4, 44, 84, etc.) so they remain visible when scrolling back
            const shouldRenderBrandTileBanner = isBrandTilePosition(i, isBrandTileBannerEnabled);
            return (
              <Fragment key={`${item.itemId}-${i}`}>
                {mpCard && <MpCard mpCard={mpCard} isLazy={!isInFirstVisibleViewport(i)} />}
                {shouldRenderBrandTileBanner && <BrandTileBanner feedIndex={i} />}
                {bannerItemPosition === i && canRenderFeedBanner && (
                  <BannerContainer position={`FEED_${activeFeedIndex}`} />
                )}
                <FeedItem
                  item={item}
                  lazyLoadImage={!isInFirstVisibleViewport(i)}
                  onClick={handleItemClick}
                  feedType={feedType}
                  feedPostCode={feedPostCode}
                  setShowLoginDialog={setShowLoginDialog}
                />
              </Fragment>
            );
          })}
        </ContentCards>
      )}
      {hasNextPage && (
        // The sentry element needs to be visible when we want to trigger loading more items
        <div ref={sentryRef}>{isLoading ? <Spinner /> : <FeedItemListSkeleton />}</div>
      )}
      {!hasNextPage && (
        <div className={Classes.loadmore}>
          <Button.Secondary onClick={() => onLoadMore(0)} disabled={isLoading}>
            {t('homepage.feeds.load_more')}
          </Button.Secondary>
        </div>
      )}
    </div>
  );
};

export default FeedItemList;
